import React, { useMemo } from 'react';
import {
  Coins,
  TrendingUp,
  TrendingDown,
  Users,
  Calendar,
  BarChart3,
  AlertCircle
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { mockCampaigns, mockTravelers, mockCashBoxTransactions } from '../../data/mockData';
import { formatDate } from '../../utils/date';

const AllCampaignsSummary = ({ dateRange }) => {
  const { t } = useTranslation();

  // Calculate comprehensive statistics
  const statistics = useMemo(() => {
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);

    // Filter campaigns within date range
    const filteredCampaigns = mockCampaigns.filter(campaign => {
      const campaignDate = new Date(campaign.startDate);
      return campaignDate >= startDate && campaignDate <= endDate;
    });

    // Calculate revenue from sold campaigns
    const soldCampaigns = filteredCampaigns.filter(c => c.type === 'sold');
    const campaignIds = filteredCampaigns.map(c => c.id);
    const totalRevenue = mockCashBoxTransactions
      .filter(transaction => transaction.transactionType === 'Payment In' && campaignIds.includes(transaction.campaignId))
      .reduce((sum, transaction) => sum + transaction.amount, 0);

    // Calculate total expenses
    const totalExpenses = mockCashBoxTransactions
      .filter(transaction => transaction.transactionType === 'Expense Out' && campaignIds.includes(transaction.campaignId))
      .reduce((sum, transaction) => sum + transaction.amount, 0);

    // Calculate outstanding debt (remaining balances)
    const totalDebt = mockCashBoxTransactions
      .filter(transaction => transaction.transactionType === 'Payment In' && campaignIds.includes(transaction.campaignId))
      .reduce((sum, transaction) => sum + (transaction.expectedAmount || transaction.amount) - transaction.amount, 0);

    // Calculate net profit
    const netProfit = totalRevenue - totalExpenses;

    // Campaign statistics
    const totalCampaigns = filteredCampaigns.length;
    const activeCampaigns = filteredCampaigns.filter(c => c.status === 'active').length;
    const completedCampaigns = filteredCampaigns.filter(c => c.status === 'completed').length;
    const cancelledCampaigns = filteredCampaigns.filter(c => c.status === 'cancelled').length;

    // Traveler statistics
    const totalTravelers = mockTravelers.filter(t => 
      campaignIds.includes(t.campaignId)
    ).length;

    // Payment statistics
    const paidPayments = mockCashBoxTransactions.filter(t => 
      campaignIds.includes(t.campaignId) && t.transactionType === 'Payment In' && t.status === 'completed'
    ).length;
    const partialPayments = mockCashBoxTransactions.filter(t => 
      campaignIds.includes(t.campaignId) && t.transactionType === 'Payment In' && t.status === 'partial'
    ).length;
    const pendingPayments = mockCashBoxTransactions.filter(t => 
      campaignIds.includes(t.campaignId) && t.transactionType === 'Payment In' && t.status === 'pending'
    ).length;

    // Expense breakdown by category
    const expensesByCategory = mockCashBoxTransactions
      .filter(transaction => campaignIds.includes(transaction.campaignId) && transaction.transactionType === 'Expense Out')
      .reduce((acc, transaction) => {
        const category = transaction.category || 'other';
        acc[category] = (acc[category] || 0) + transaction.amount;
        return acc;
      }, {});

    // Monthly revenue trend (last 6 months)
    const monthlyRevenue = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      
      const monthCampaigns = soldCampaigns.filter(campaign => {
        const campaignDate = new Date(campaign.startDate);
        return campaignDate >= monthStart && campaignDate <= monthEnd;
      });
      
      const monthRevenue = mockCashBoxTransactions
        .filter(transaction => 
          transaction.transactionType === 'Payment In' &&
          monthCampaigns.some(campaign => campaign.id === transaction.campaignId)
        )
        .reduce((sum, transaction) => sum + transaction.amount, 0);
      
      monthlyRevenue.push({
        month: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        revenue: monthRevenue
      });
    }

    return {
      totalRevenue,
      totalExpenses,
      totalDebt,
      netProfit,
      totalCampaigns,
      activeCampaigns,
      completedCampaigns,
      cancelledCampaigns,
      totalTravelers,
      paidPayments,
      partialPayments,
      pendingPayments,
      expensesByCategory,
      monthlyRevenue,
      profitMargin: totalRevenue > 0 ? ((netProfit / totalRevenue) * 100) : 0
    };
  }, [dateRange]);

  const StatCard = ({ title, value, icon, color, trend, trendValue }) => {
    const Icon = icon;
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
              {title}
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">
              {typeof value === 'number' && (title.toLowerCase().includes('amount') || title.toLowerCase().includes('revenue') || title.toLowerCase().includes('profit') || title.toLowerCase().includes('debt') || title.toLowerCase().includes('expense'))
                ? `${value.toLocaleString()} IQD`
                : value.toLocaleString()}
            </p>
            {trend && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${
                trend === 'up' ? 'text-green-600' : 'text-red-600'
              }`}>
                {trend === 'up' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                <span>{trendValue}%</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${color} flex-shrink-0 ml-4`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <BarChart3 className="w-8 h-8 text-blue-600" />
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('reports.allCampaignsSummary')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {t('reports.dateRangeDisplay', { 
              start: formatDate(dateRange.startDate),
              end: formatDate(dateRange.endDate)
            })}
          </p>
        </div>
      </div>

      {/* Financial Overview */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('reports.financialOverview')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title={t('reports.totalRevenue')}
            value={statistics.totalRevenue}
            icon={Coins}
            color="bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400"
            trend="up"
            trendValue="12.5"
          />
          <StatCard
            title={t('reports.totalExpenses')}
            value={statistics.totalExpenses}
            icon={TrendingDown}
            color="bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400"
          />
          <StatCard
            title={t('reports.outstandingDebt')}
            value={statistics.totalDebt}
            icon={AlertCircle}
            color="bg-yellow-100 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400"
          />
          <StatCard
            title={t('reports.netProfit')}
            value={statistics.netProfit}
            icon={TrendingUp}
            color={statistics.netProfit >= 0 
              ? "bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400"
              : "bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400"
            }
            trend={statistics.netProfit >= 0 ? 'up' : 'down'}
            trendValue={Math.abs(statistics.profitMargin).toFixed(1)}
          />
        </div>
      </div>

      {/* Campaign Statistics */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('reports.campaignStatistics')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title={t('reports.totalCampaigns')}
            value={statistics.totalCampaigns}
            icon={Calendar}
            color="bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
          />
          <StatCard
            title={t('reports.activeCampaigns')}
            value={statistics.activeCampaigns}
            icon={TrendingUp}
            color="bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400"
          />
          <StatCard
            title={t('reports.completedCampaigns')}
            value={statistics.completedCampaigns}
            icon={Calendar}
            color="bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400"
          />
          <StatCard
            title={t('reports.totalTravelers')}
            value={statistics.totalTravelers}
            icon={Users}
            color="bg-cyan-100 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400"
          />
        </div>
      </div>

      {/* Payment Status Overview */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('reports.paymentStatusOverview')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard
            title={t('reports.paidPayments')}
            value={statistics.paidPayments}
            icon={Coins}
            color="bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400"
          />
          <StatCard
            title={t('reports.partialPayments')}
            value={statistics.partialPayments}
            icon={AlertCircle}
            color="bg-yellow-100 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400"
          />
          <StatCard
            title={t('reports.pendingPayments')}
            value={statistics.pendingPayments}
            icon={TrendingDown}
            color="bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400"
          />
        </div>
      </div>

      {/* Expense Breakdown */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('reports.expenseBreakdown')}
        </h3>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
          <div className="space-y-4">
            {Object.entries(statistics.expensesByCategory).map(([category, amount]) => {
              const percentage = statistics.totalExpenses > 0 
                ? ((amount / statistics.totalExpenses) * 100).toFixed(1)
                : 0;
              
              return (
                <div key={category} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {t(`expenses.categories.${category}`)}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-400 w-12">
                      {percentage}%
                    </span>
                    <span className="font-semibold text-gray-900 dark:text-white w-20 text-right">
                      ${amount.toLocaleString()}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Monthly Revenue Trend */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('reports.monthlyRevenueTrend')}
        </h3>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-end justify-between h-64 gap-2">
            {statistics.monthlyRevenue.map((month, index) => {
              const maxRevenue = Math.max(...statistics.monthlyRevenue.map(m => m.revenue));
              const height = maxRevenue > 0 ? (month.revenue / maxRevenue) * 100 : 0;
              
              return (
                <div key={index} className="flex flex-col items-center gap-2 flex-1">
                  <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                    ${month.revenue.toLocaleString()}
                  </div>
                  <div 
                    className="w-full bg-blue-500 rounded-t transition-all duration-300 min-h-[4px]"
                    style={{ height: `${Math.max(height, 2)}%` }}
                  ></div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 text-center">
                    {month.month}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllCampaignsSummary;